import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EventState, EventFilters, Event } from './types'
import {
  fetchEvents,
  fetchEventDetails,
  createEvent,
  updateEvent,
  updateInvitedTeamMember,
} from './actions'

const initialState: EventState = {
  events: [],
  selectedEvent: {} as Event,
  loading: false,
  updateLoading: false,
  selectedEventLoading: false,
  error: null,
  selectedEventError: null,
  filters: {
    category: [],
    importance: [],
    status: [],
  },
  searchQuery: '',
  loadingUpdateInvitedTeamMember: false,
  errorUpdateInvitedTeamMember: null,
}

const manageEventsSlice = createSlice({
  name: 'manageEvents',
  initialState,
  reducers: {
    clearSelectedEvent: (state) => {
      state.selectedEvent = {} as Event
    },
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<EventFilters>) => {
      state.filters = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch Events
    builder.addCase(fetchEvents.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.loading = false
      state.events = action.payload
    })
    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })

    // Fetch Event Details
    builder.addCase(fetchEventDetails.pending, (state) => {
      state.selectedEventLoading = true
      state.selectedEventError = null
    })
    builder.addCase(fetchEventDetails.fulfilled, (state, action) => {
      state.selectedEventLoading = false
      state.selectedEvent = action.payload
    })
    builder.addCase(fetchEventDetails.rejected, (state, action) => {
      state.selectedEventLoading = false
      state.selectedEventError = action.payload as string
    })

    // Create Event
    builder.addCase(createEvent.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(createEvent.fulfilled, (state, action) => {
      state.loading = false
      state.events.push(action.payload)
    })
    builder.addCase(createEvent.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message || 'Failed to create event'
    })

    // Update Event
    builder.addCase(updateEvent.pending, (state) => {
      state.updateLoading = true
      state.error = null
    })
    builder.addCase(updateEvent.fulfilled, (state, action) => {
      state.updateLoading = false
      const index = state.events.findIndex((event) => event._id === action.payload._id)
      if (index !== -1) {
        state.events[index] = action.payload
      }
    })
    builder.addCase(updateEvent.rejected, (state, action) => {
      state.updateLoading = false
      state.error = action.error.message || 'Failed to update event'
    })

    builder.addCase(updateInvitedTeamMember.pending, (state) => {
      state.loadingUpdateInvitedTeamMember = true
      state.errorUpdateInvitedTeamMember = ''
    })
    builder.addCase(updateInvitedTeamMember.fulfilled, (state) => {
      state.loadingUpdateInvitedTeamMember = false
      state.errorUpdateInvitedTeamMember = ''
    })
    builder.addCase(updateInvitedTeamMember.rejected, (state, action) => {
      state.loadingUpdateInvitedTeamMember = false
      state.errorUpdateInvitedTeamMember = action.error.message || 'Failed to update event'
    })

    // Add more cases for other actions as needed
  },
})

export const { clearSelectedEvent, clearError, setSearchQuery, setFilters } = manageEventsSlice.actions
export default manageEventsSlice.reducer
